import * as React from 'react';
import { createPortal } from 'react-dom';
import { DebugBadge } from './DebugBadge';
import { ViolationBadge } from './ViolationBadge';
import { useViolationScan } from './ViolationScanner';

const DEBUG_PARAM = 'debug';
const DEBUG_VALUE_COMPONENTS = 'components';
const DEBUG_VALUE_VIOLATIONS = 'violations';

export type DebugOverlayMode = 'off' | 'components' | 'violations';

type DebugOverlayState = {
  enabled: boolean;
  mode: DebugOverlayMode;
};

const DebugOverlayContext = React.createContext<DebugOverlayState>({ enabled: false, mode: 'off' });

export function useDebugOverlay(): DebugOverlayState {
  return React.useContext(DebugOverlayContext);
}

function readModeFromUrl(): DebugOverlayMode {
  if (typeof window === 'undefined') return 'off';
  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get(DEBUG_PARAM);
    if (v === DEBUG_VALUE_VIOLATIONS) return 'violations';
    if (v === DEBUG_VALUE_COMPONENTS) return 'components';
    return 'off';
  } catch {
    return 'off';
  }
}

export type DebugOverlayProviderProps = {
  children: React.ReactNode;
};

/**
 * Provider for debug overlay. When enabled (?debug=components or Ctrl+Shift+D),
 * in components mode shows a single badge for the hovered element (elementsFromPoint + data-component).
 * Must NOT affect layout when disabled. Only active when NODE_ENV !== 'production'.
 */
function cycleMode(m: DebugOverlayMode): DebugOverlayMode {
  if (m === 'off') return 'components';
  if (m === 'components') return 'violations';
  return 'off';
}

export function DebugOverlayProvider({ children }: DebugOverlayProviderProps): React.ReactElement {
  const [mode, setMode] = React.useState<DebugOverlayMode>('off');
  const enabled = mode !== 'off';
  const [hoveredBadge, setHoveredBadge] = React.useState<{
    component: string;
    packageName: string;
    rect: DOMRect;
  } | null>(null);
  const { structural: structuralViolations, token: tokenViolations } = useViolationScan(mode === 'violations');

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const fromUrl = readModeFromUrl();
    setMode(fromUrl);
  }, []);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'D' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        setMode((prev) => cycleMode(prev));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production' || mode !== 'components') {
      setHoveredBadge(null);
      return;
    }

    const onMouseMove = (e: MouseEvent) => {
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      const target = elements.find((el) => el.getAttribute('data-component'));
      if (target) {
        const component = target.getAttribute('data-component') ?? 'Unknown';
        const packageName = target.getAttribute('data-package') ?? '';
        const rect = target.getBoundingClientRect();
        setHoveredBadge({ component, packageName, rect });
      } else {
        setHoveredBadge(null);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    return () => document.removeEventListener('mousemove', onMouseMove);
  }, [mode]);

  const state = React.useMemo(() => ({ enabled, mode }), [enabled, mode]);

  const showComponents = mode === 'components' && typeof document !== 'undefined' && document.body;
  const showViolations = mode === 'violations' && typeof document !== 'undefined' && document.body;
  const allViolations = [...structuralViolations, ...tokenViolations];

  const overlay =
    process.env.NODE_ENV !== 'production' && enabled && typeof document !== 'undefined' && document.body ? (
      createPortal(
        <div className="fixed inset-0 pointer-events-none z-[9998]" aria-hidden>
          {showComponents &&
            hoveredBadge && (
              <>
                <div
                  className="fixed pointer-events-none z-[9997] outline-1 outline-dashed outline-white/30 rounded-sm"
                  style={{
                    left: hoveredBadge.rect.left,
                    top: hoveredBadge.rect.top,
                    width: hoveredBadge.rect.width,
                    height: hoveredBadge.rect.height
                  } as React.CSSProperties}
                />
                <DebugBadge
                  component={hoveredBadge.component}
                  packageName={hoveredBadge.packageName}
                  rect={hoveredBadge.rect}
                />
              </>
            )}
          {showViolations && (
            <>
              {structuralViolations.map((v, i) => (
                <div
                  key={`s-${i}`}
                  className="fixed pointer-events-none z-[9997] outline-2 outline outline-danger"
                  style={{
                    left: v.rect.left,
                    top: v.rect.top,
                    width: v.rect.width,
                    height: v.rect.height
                  } as React.CSSProperties}
                />
              ))}
              {tokenViolations.map((v, i) => (
                <div
                  key={`t-${i}`}
                  className="fixed pointer-events-none z-[9997] outline-2 outline outline-warn"
                  style={{
                    left: v.rect.left,
                    top: v.rect.top,
                    width: v.rect.width,
                    height: v.rect.height
                  } as React.CSSProperties}
                />
              ))}
              {allViolations.map((v, i) => (
                <ViolationBadge key={`vb-${i}`} kind={v.kind} message={v.message} rect={v.rect} />
              ))}
            </>
          )}
        </div>,
        document.body
      )
    ) : null;

  return (
    <DebugOverlayContext.Provider value={state}>
      {children}
      {overlay}
    </DebugOverlayContext.Provider>
  );
}
